import { SchemeEnum } from '@enums'
import VehicleReg from 'components/VehicleReg'
import {
  COMMERCIAL_VEHICLE_SCHEME,
  COMMERCIAL_VEHICLE_WEIGHT_BREAKPOINT,
  IMPOUNDED_COMMERCIAL_VEHICLE_SCHEME,
  IMPOUNDED_PRIVATE_CAR_SCHEME,
  MOTORCYCLE,
  MOTORCYCLE_VEHICLE_SCHEME,
  PRIVATE_CAR_SCHEME,
  VEHICLE_LEARNER_SCHEME,
} from 'containers/Vehicle/constants'
import VehicleModal from 'containers/VehicleModal'
import { useScheme, useVehicle } from 'hooks'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom'
import useCustomerAccount from 'hooks/useCustomerAccount'
import { VehicleValue } from 'components/VehicleValue'
import { updateVehicleValue } from 'containers/Vehicle/actions'
import { updateRiskData } from 'containers/RiskDataCollectionPage/actions'
import { useDispatch } from 'react-redux'
import { FormikContext } from 'formik'
import { setAmplitudeUserRegProperty } from 'utils/analytics-helpers'
import { SummarySection } from 'containers/QuoteSummary/SummarySectionStyles'
import { ErrorMessage } from 'components/common/ErrorMessage'

interface VehicleSummaryProps {
  id: string
  handleUpdate: () => void
  isIndividual?: boolean
  hideValue?: boolean
}

const VehicleSummary: React.FC<VehicleSummaryProps> = ({
  id,
  handleUpdate,
  hideValue = false,
}) => {
  const [isModalVisible, setModalVisible] = useState(false)

  const { vehicle, vehicleValue } = useVehicle()
  const { scheme } = useScheme()
  const history = useHistory()

  const { isCustomerAccountQuote } = useCustomerAccount()

  const registrationNumber = vehicle.selected.RegistrationNumber || ''
  setAmplitudeUserRegProperty(vehicle.selected.RegistrationNumber)

  const vehicleSummary = useMemo(() => {
    switch (scheme) {
      case PRIVATE_CAR_SCHEME:
        if (vehicle.selected.Variant) {
          return `${vehicle.selected.Variant}`
        }
        return `${vehicle.selected.Make} ${vehicle.selected.Model}`

      case COMMERCIAL_VEHICLE_SCHEME:
      case SchemeEnum.COURIER:
        if (
          vehicle.selected.GrossWeight &&
          Number(vehicle.selected.GrossWeight) >
            COMMERCIAL_VEHICLE_WEIGHT_BREAKPOINT
        ) {
          return `Weight: ${vehicle.selected.GrossWeight}kg Year: ${vehicle.selected.YearOfManufacture}`
        }
        if (vehicle.selected.Variant) {
          return `${vehicle.selected.Variant}`
        }
        return `${vehicle.selected.Make} ${vehicle.selected.Model}`

      case VEHICLE_LEARNER_SCHEME:
      case IMPOUNDED_COMMERCIAL_VEHICLE_SCHEME:
      case IMPOUNDED_PRIVATE_CAR_SCHEME:
      case MOTORCYCLE_VEHICLE_SCHEME:
        if (vehicle.selected.Variant) {
          return `${vehicle.selected.Variant}`
        }
        return `${vehicle.selected.Make} ${vehicle.selected.Model}`

      default:
        return ''
    }
  }, [vehicle, scheme])

  const toggleVehicleModal = () => {
    if (isCustomerAccountQuote) {
      window.location.href = `/quote/driver-details/logged-in${history.location.search}`
    } else {
      setModalVisible(!isModalVisible)
    }
  }

  const onUpdate = () => {
    handleUpdate()
    toggleVehicleModal()
  }

  const dispatch = useDispatch()

  // VehicleSummary is rendered both inside the Formik-wrapped Driver Details
  // form and on other pages (e.g. QuoteSummary) with no Formik ancestor, so
  // this context may be undefined - every usage below must guard for that.
  // Using useContext directly (rather than useFormikContext) since that's an
  // intentional, correct case here, not the usage mistake Formik's own
  // invariant warning assumes.
  const formikContext = useContext<any>(FormikContext)
  const setFieldValue = formikContext?.setFieldValue
  const [showVehicleValueError, setShowVehicleValueError] = useState(false)

  useEffect(() => {
    setFieldValue?.('vehicleValue', vehicleValue)
  }, [vehicleValue, setFieldValue])

  // Other fields on this page show their errors via FormField/FormFieldContent,
  // which flips its local isError state to true once form.isSubmitting has
  // been true at least once (a "blip" that latches, since it never resets to
  // false again). We can't use FormField/<Field> here since VehicleSummary
  // isn't always inside a Formik tree, so this replicates that same latch
  // manually. submitCount can't be used instead: the "Continue" button
  // rendered just above this (MarketingPreferencesButton.tsx) is type='button'
  // with a custom onClick that calls validateForm() + setSubmitting(true)
  // directly, bypassing Formik's submitForm()/SUBMIT_ATTEMPT entirely - the
  // only place submitCount is ever incremented. Confirmed via logging: on a
  // real click, isSubmitting flips true while submitCount stays 0.
  useEffect(() => {
    if (formikContext?.isSubmitting) {
      setShowVehicleValueError(true)
    }
  }, [formikContext?.isSubmitting])

  const handleVehicleValueChange = (value: any) => {
    dispatch(
      updateVehicleValue({
        value: value.target.value,
        description: 'VehicleValue',
      }),
    )
    setFieldValue?.('vehicleValue', value.target.value)
    setAmplitudeUserRegProperty(value.target.value)
  }

  // TODO: Revisit this, at the minute we are just
  // reusing logic from before to store descriptions in redux.
  const handleSelect = (name: string, value: string, label: string) => {
    // We need to capitalise first letter, as in state they are using capitals at the start
    const capitalisedName = name.charAt(0).toUpperCase() + name.slice(1)

    switch (capitalisedName) {
      case 'VehicleValue':
        setFieldValue?.('vehicleValue', value)
        return dispatch(
          updateVehicleValue({
            value,
            description: label,
          }),
        )
      default:
        return dispatch(
          updateRiskData({
            [capitalisedName]: {
              value,
              description: label,
            },
          }),
        )
    }
  }

  return (
    <>
      <SummarySection id={id} onClick={toggleVehicleModal}>
        <p
          style={{ textAlign: 'center' }}
          className='quote-summary-title vehicle'
        >
          Vehicle to be covered
        </p>

        <p className='quote-summary-text vehicle' data-tracking-scheme={scheme}>
          {vehicleSummary}
        </p>
        <VehicleReg reg={registrationNumber} />
        <i className='fa fa-pencil-alt' />
        {!hideValue && (
          <div>
            <p style={{ marginBottom: 0 }}>Vehicle value</p>
            <VehicleValue
              onSelect={handleSelect}
              onChange={handleVehicleValueChange}
              value={vehicleValue}
              isMotorcycle={vehicle.selected.VehicleType === MOTORCYCLE}
            />
            {showVehicleValueError && formikContext?.errors?.vehicleValue && (
              <ErrorMessage>
                Please select an estimated vehicle value
              </ErrorMessage>
            )}
          </div>
        )}
      </SummarySection>
      {!isCustomerAccountQuote && (
        <VehicleModal
          isVisible={isModalVisible}
          handleUpdate={onUpdate}
          hideModal={toggleVehicleModal}
        />
      )}
    </>
  )
}
export default VehicleSummary
